/**
 * Normalizes per-game admin settings (getGameSettings) into a small, display-ready
 * shape for the landing page: ranks, characters (legends/agents), roles, game modes,
 * regions. Settings keys are game-prefixed (e.g. `apex-rank`, `valorant-character`,
 * `cs2-premierrank`), so we match by fuzzy key/title instead of exact keys.
 */

type SettingValueRaw = {
  name?: string | null;
  badgeImage?: string | null;
  decorImage?: string | null;
  tagImage?: string | null;
};

type SettingRaw = {
  key?: string | null;
  title?: string | null;
  values?: (SettingValueRaw | null)[] | null;
};

export type FacetValue = {
  name: string;
  image: string | null;
};

export type GameFacets = {
  ranks: FacetValue[];
  characters: FacetValue[];
  charactersLabel: string;
  roles: FacetValue[];
  gameModes: FacetValue[];
  regions: FacetValue[];
  versions: FacetValue[];
};

const EMPTY_FACETS: GameFacets = {
  ranks: [],
  characters: [],
  charactersLabel: 'Characters',
  roles: [],
  gameModes: [],
  regions: [],
  versions: [],
};

const pickImage = (value: SettingValueRaw): string | null =>
  value.tagImage || value.badgeImage || value.decorImage || null;

const toValues = (setting: SettingRaw | undefined): FacetValue[] =>
  (setting?.values ?? [])
    .filter((v): v is SettingValueRaw => Boolean(v && v.name))
    .map((v) => ({ name: (v.name ?? '').trim(), image: pickImage(v) }));

/** Turn a messy admin title ("Favorite legend (max 2)", "Agents ") into a clean noun. */
const cleanCharactersLabel = (title: string | null | undefined): string => {
  const t = (title ?? '').toLowerCase();
  if (t.includes('agent')) return 'Agents';
  if (t.includes('legend')) return 'Legends';
  if (t.includes('hero')) return 'Heroes';
  if (t.includes('operator')) return 'Operators';
  if (t.includes('character')) return 'Characters';
  return 'Characters';
};

export const parseGameFacets = (
  settings: readonly (SettingRaw | null)[] | null | undefined
): GameFacets => {
  const list = (settings ?? []).filter((s): s is SettingRaw => Boolean(s));
  if (list.length === 0) {
    return EMPTY_FACETS;
  }

  const byKeyOrTitle = (re: RegExp): SettingRaw | undefined =>
    list.find((s) => re.test(s.key ?? '') || re.test(s.title ?? ''));

  const rank = byKeyOrTitle(/rank/i);
  // Character-like set: legends / agents / heroes / operators. Exclude the `-decor`
  // banner set which sometimes reuses character names but is a decorative frame list.
  const character = list.find((s) => {
    const key = s.key ?? '';
    if (/decor/i.test(key)) return false;
    return /charact|agent|legend|hero|operator/i.test(key) || /agent|legend|hero|operator/i.test(s.title ?? '');
  });
  // `position` covers sports-style games (Rocket League: striker / defender / flex).
  const role = byKeyOrTitle(/(^|-)(role|class|playstyle|position)/i);
  // `playlist` is what queue-based games call their modes (Rocket League: duel / doubles / hoops).
  const gameMode = byKeyOrTitle(/(game.?mode|playlist)/i);
  const region = byKeyOrTitle(/region/i);
  // Games that ship as separate, non-crossplay editions (World of Warcraft: Midnight / Forever /
  // Classic) keep the edition in a `-version` key. Matched by key only: titles arrive translated.
  const version = list.find((s) => /(^|-)(version|edition)s?$/i.test(s.key ?? ''));

  return {
    ranks: toValues(rank),
    characters: toValues(character),
    charactersLabel: cleanCharactersLabel(character?.title),
    roles: toValues(role),
    gameModes: toValues(gameMode),
    regions: toValues(region),
    versions: toValues(version),
  };
};
